import React, { useState } from 'react';
import { Keyboard, TouchableOpacity } from "react-native"
import { Icons, Toggle } from '../../Components';
import Colors from '../../constants/colors';
import { AuthenticateApi, AuthenticateHelpers } from '../api';
import type { AuthenticatedProps } from '../api/types';
import { Text, View, Button, TextInput } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import type { FocusPositionProps } from '../../types';


type LoginFormProps = {
    visible:boolean,
    maxWidth?:number,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onTryAuthenticate:(attribute:string, result:'success'|'fail') => void,
    onRegister: () => void,
    onAuthenticated: (auth_response:AuthenticatedProps) => void
}


const LoginForm = ({ maxWidth, visible, onRegister, onFocusPosition, onAuthenticated, onTryAuthenticate }:LoginFormProps) => {
    const C = useColors();
    const [ auth_check, setAuthCheck ] = useState<{
        loading:boolean,
        attribute: 'username'|'email'|'phone',
        value:string
    }>({
        loading:false,
        attribute:'username',
        value:''
    });
    const [ halted, setHalted ] = useState(false);
    const [ login_try, setLoginTry ] = useState<{
        pass_token: string,
        loading:boolean,
        visible:boolean,
        verification_session_id?:string,
        type: 'token' | 'password',
        error?:string
    }>({
        pass_token: '',
        loading:false,
        visible:false,
        type: 'password'
    })
    const [ auth_check_error, setAuthCheckError ] = useState<string>();
    
    const { loading, attribute, value } = auth_check
    const { pass_token, type, verification_session_id } = login_try;

    const handleAuthCheck = async() => {
        if(loading){ return } //No mashy mashy
        Keyboard.dismiss();
        setAuthCheck({ ...auth_check, loading:true })
        const { result, message, verification_session_id, next } = await AuthenticateApi.authCheck(attribute, value);
        if(result == 'fail'){
            setAuthCheckError(message)
            onTryAuthenticate(attribute, 'fail')
        } else {
            onTryAuthenticate(attribute, 'success')
            setLoginTry({
                pass_token: '',
                verification_session_id: verification_session_id,
                visible:true,
                loading:false,
                type: next ?? 'password'
            })
        }
        setAuthCheck({ ...auth_check, loading:false })
    }

    const handleForgot = async() => {
        Keyboard.dismiss();
        const resp = await AuthenticateApi.authForgot(attribute, value)
        if(resp.result == 'fail'){
            setAuthCheckError(resp.message)
        } else {
            setLoginTry({
                pass_token: '',
                verification_session_id: resp.verification_session_id,
                visible:true,
                loading:false,
                type: resp.next ?? 'password'
            })
        }
    }

    const handleLogin = async() => {
        Keyboard.dismiss();
        setLoginTry({ ...login_try, loading:true })
        const { result, player_id, new_player, message, access_token, refresh_token, expire_datetime } = await AuthenticateApi.authLogin(attribute, value, pass_token, verification_session_id)

        if(result == 'fail' || !access_token || !refresh_token || !player_id){
            setLoginTry({ ...login_try, error:message, loading:false })
            setHalted(true)
            return setTimeout(() => {
                setHalted(false);
            }, 5000);
        }
        setLoginTry({ ...login_try, loading:false, error:undefined })
        return onAuthenticated({
            player_id,
            access_token,
            refresh_token,
            expire_datetime,
            new_player
        })
    }

    const getAttributeIcon = () => {
        switch(attribute){
            case 'email': return <Icons.EmailIcon size={20} color={C.text.h1}/>
            case 'phone': return <Icons.PhoneIcon size={20} color={C.text.h1} />
            case 'username': return <Icons.UserIcon size={20} color={C.text.h1} />
            default: return <Icons.UserIcon size={20} color={C.text.h1} />
        }
    }

    const { valid } = AuthenticateHelpers.isAttributeValid(attribute, value);
    const login_valid = pass_token.length > 0 && !halted ? true : false;

    if(!visible){ return <></> }

    return (
        <View style={{ flexGrow:1, maxWidth }}>
            {!login_try.visible ?
            <View style={{ flexGrow:1 }}>
                <View style={{ padding:20 }}>
                    <View>
                        <Text theme='h1'>Login Using My:</Text>
                    </View>
                    <View style={{ marginTop:10 }}>
                        <Toggle
                            options={[
                                { key: 'username', label: 'Username' },
                                { key: 'email', label: 'Email'},
                                { key: 'phone', label: 'Phone' }
                            ]}
                            selected_option={attribute}
                            onSelectOption={(a) => setAuthCheck({ ...auth_check, attribute: a as 'username'|'email'|'phone' })}
                        />
                    </View>
                </View>
                <View style={{ flexGrow:1 }}>
                <View style={{padding:30 }}>
                    <View style={{  marginBottom:10 }}>
                        <Text theme='h1'>ENTER {attribute.toUpperCase()}</Text>
                        <Text style={{ marginTop:5 }} theme='h2'>Please enter your {attribute} to continue.</Text>
                    </View>
                    <View type='input' style={{ marginTop:5, flexDirection:'row', alignItems:'center' }}>
                        <View transparent style={{ paddingLeft:10, paddingRight:10 }}>
                            {getAttributeIcon()}
                        </View>
                        <TextInput
                            style={{ flex:1 }}
                            onFocusPosition={onFocusPosition}
                            value={value}
                            transparent
                            placeholder={attribute.toUpperCase()}
                            autoFocus={true}
                            placeholderTextColor={Colors.brand.slate}
                            onChangeText={(text) => {
                                setAuthCheck({ ...auth_check, value: text })
                            }}
                        />
                        <View transparent style={{ padding:10 }}>
                            { valid ?
                            <Icons.CheckCirlceIcon color={C.text.success} size={14} />
                            :
                            <Icons.AlertIcon color={C.text.warning} size={20} />
                            }
                        </View>
                    </View>
                    {auth_check_error ?
                        <TouchableOpacity style={{ marginTop:10, padding:10, backgroundColor:Colors.shades.white, borderRadius:8 }} onPress={() => onRegister()}>
                            <Text size={14} color={Colors.utility.error} weight='semibold' textAlign='center'>{auth_check_error}</Text>
                            <Text style={{ marginTop:5 }} size={14} color={Colors.brand.electric} weight='bold' textAlign='center'>SIGN UP HERE</Text>
                        </TouchableOpacity>
                    :<></>}
                    </View>
                </View>
                <View type='footer' style={{ padding:10, paddingLeft:20, paddingRight:20 }}>
                    <Button
                        title='LOGIN'
                        style={{ opacity:!valid||loading?0.5:1 }}
                        loading={loading}
                        disabled={!valid||loading}
                        type='success'
                        onPress={() => handleAuthCheck()}
                    />
                </View>
                <View style={{ padding:10 }}>
                    <Button
                        title='Are You New?  Sign Up Here'
                        type='text'
                        onPress={() => onRegister()}
                    />
                </View>
            </View>
            :
            <View style={{ flexGrow:1 }}>
                <View style={{ flexGrow:1 }}>
                    <View style={{  borderRadius:8, padding:30 }}>
                    <View style={{  marginBottom:10 }}>
                        <Text theme='h1'>MY {attribute.toUpperCase()}</Text>
                    </View>   
                    <View type='input' style={{ flexDirection:'row', alignItems:'center' }}>
                        <View transparent style={{ paddingLeft:10, paddingRight:15 }}>
                            {getAttributeIcon()}
                        </View>
                        <TextInput
                            value={value}
                            transparent
                            onFocusPosition={onFocusPosition}
                            editable={false}
                            style={{ flex:1  }}
                            placeholderTextColor={Colors.brand.slate}
                            onChangeText={() => console.log('')}
                        />
                        <View transparent style={{ padding:10 }}>
                            <Icons.CheckCirlceIcon color={C.text.success} size={14} />
                        </View>
                    </View>
                    </View>
                    <View style={{ borderRadius:8, padding:30 }}>
                        <View style={{  marginBottom:10 }}>
                            <Text theme='h1'>ENTER {type.toUpperCase()}</Text>
                            {type == 'token' ?
                            <Text style={{ marginTop:5 }} theme='h2'>We sent a one-time token to {value}.  Please enter it below.</Text>
                            :
                            <Text style={{ marginTop:5 }} theme='h2'>Please enter the password associated with the {attribute} {value}.</Text>
                            }
                        </View>
                        <View type='input' style={{ flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:10, paddingRight:15 }}>
                                <Icons.LockClosedIcon
                                    size={20}
                                    color={C.text.h1}
                                />
                            </View>
                            <TextInput
                                style={{ flex:1 }}
                                key={type}
                                onFocusPosition={onFocusPosition}
                                value={pass_token}
                                placeholder={`Enter ${type}`}
                                autoFocus={true}
                                secureTextEntry
                                textContentType={type=='password'?'password':'oneTimeCode'}
                                placeholderTextColor={Colors.brand.slate}
                                onChangeText={(text) => {
                                    setLoginTry({
                                        ...login_try,
                                        pass_token: text
                                    })
                                }}
                            />
                            <View transparent style={{ padding:10 }}>
                                { login_valid ?
                                <Icons.CheckCirlceIcon color={C.text.h1} size={14} />
                                :
                                <Icons.AlertIcon color={C.text.warning} size={18} />
                                }
                            </View>
                        </View>
                        {login_try.error ?
                        <TouchableOpacity style={{ marginTop:10, padding:10, backgroundColor:Colors.shades.white, borderRadius:8 }} onPress={() => handleForgot()}>
                            <Text size={14} color={Colors.utility.error} weight='semibold' textAlign='center'>{login_try.error}</Text>
                            <Text style={{ marginTop:5 }} size={14} color={Colors.brand.electric} weight='bold' textAlign='center'>SEND ME A CODE INSTEAD</Text>
                        </TouchableOpacity>
                        :<></>}
                    </View>
                </View>
                <View style={{ padding:20, flexDirection:'row' }}>
                    <Button
                        title='BACK'
                        style={{ marginRight:5, flex:1 }}
                        title_color={Colors.brand.electric}
                        type='close'
                        onPress={() => {
                            setLoginTry({ ...login_try, pass_token: '', visible:false });
                        }}
                    />
                    <Button
                        title={halted?'Try again in 5 seconds':'LOGIN'}
                        style={{ flex:3, opacity:!login_valid||loading?0.5:1 }}
                        title_color={Colors.shades.white}
                        loading={login_try.loading}
                        type='success'
                        disabled={!login_valid||loading}
                        onPress={() => handleLogin()}
                    />
                </View>
            </View>
        }
        </View>
    )
}

export default LoginForm